export type ShowcaseAssetLink = {
  label: string;
  href: string;
  kind: "audio" | "captions";
};

export type ShowcaseRelease = {
  slug: string;
  artist: string;
  title: string;
  duration: string;
  style: string;
  theme: string;
  tagline: string;
  useCase: string;
  accentClass: string;
  proof: readonly string[];
  assetLinks?: readonly ShowcaseAssetLink[];
};

export const showcaseReleases = [
  {
    slug: "silk-on-fire-front-face-cut",
    artist: "VÉLA",
    title: "Silk On Fire",
    duration: "2:57",
    style: "Front-face runway soul-pop cinematic MV",
    theme: "Backstage front-face walk, flash-wall exits, rain boundaries, dry black-car glass, card-table refusal, and sunrise self-claiming",
    tagline: "A front-face repair cut of VÉLA's runway release with clearer singer visibility, rebuilt scene physics, no-mouth direction, and line-timed lyric captions.",
    useCase: "Front-face cinematic MV repair",
    accentClass: "showcase-c",
    proof: ["Front-face repair cut", "25-scene cinematic story", "Physics-checked captions"],
    assetLinks: [
      {
        label: "SRT captions",
        href: "/showcase-captions/silk-on-fire-front-face-cut.srt",
        kind: "captions"
      }
    ]
  },
  {
    slug: "silk-on-fire",
    artist: "VÉLA",
    title: "Silk On Fire",
    duration: "2:57",
    style: "Runway soul-pop cinematic MV",
    theme: "Backstage runway, flash-wall exits, rain boundaries, black-car glass, gallery refusal, and dawn self-claiming",
    tagline: "A Suno full-length VÉLA release built from trend research, a fictional runway-idol character sheet, physics-first Grok scenes, no-mouth direction, and Whisper-timed lyric captions.",
    useCase: "Trend-to-release runway MV",
    accentClass: "showcase-a",
    proof: ["Suno full-length single", "Runway-idol character sheet", "Physics-first no-mouth MV"]
  },
  {
    slug: "dont-turn-around",
    artist: "VÉLA",
    title: "Don't Turn Around",
    duration: "4:29",
    style: "No-dialogue cinematic soul-pop MV",
    theme: "Blue kitchen light, rain-glass departures, hotel corridors, rooftop distance, and a dawn walkaway",
    tagline: "A no-mouth VÉLA release built to avoid shaky lip-sync: side-profile, back-view, silent acting, Whisper-timed captions, and physical scene prompts.",
    useCase: "No-mouth cinematic MV",
    accentClass: "showcase-c",
    proof: ["No dialogue direction", "Silent acting shots", "Whisper-timed captions"]
  },
  {
    slug: "midnight-looks-good-on-me-red-cut",
    artist: "VÉLA",
    title: "Midnight Looks Good On Me: Red Cut",
    duration: "2:11",
    style: "Late-night soul-pop fashion MV",
    theme: "Rain-glass city, black car escape, red marble entrance, cocktail-bar refusal, and silver-lit self-claiming",
    tagline: "A physics-repaired cinematic VÉLA cut with stronger scene geometry, same-character styling, and Whisper-timed lyric captions.",
    useCase: "Trend-to-release MV repair",
    accentClass: "showcase-b",
    proof: ["Physics-repaired scenes", "Same-character story", "Outlined lyric captions"]
  },
  {
    slug: "call-me-when-youre-outside",
    artist: "VÉLA",
    title: "Call Me When You're Outside",
    duration: "2:11",
    style: "Late-night soul-pop cinematic single",
    theme: "Phone-light promise, city doorway, taxi glass, rooftop release, and self-claiming after midnight",
    tagline: "A same-character VÉLA release built from trend-led hook writing, custom lyrics, Whisper-timed captions, and polished Zolo Music publishing.",
    useCase: "Trend-to-release MV",
    accentClass: "showcase-a",
    proof: ["Trend-led hook", "Same-character story", "Whisper-timed captions"]
  },
  {
    slug: "red-room-walkaway",
    artist: "VÉLA",
    title: "Red Room Walkaway",
    duration: "2:11",
    style: "Soul-pop cinematic idol cut",
    theme: "Red-room pressure, flash corridor, stairwell escape, and rooftop self-claiming",
    tagline: "A refined same-character idol cut with repaired scene geography, visible opening visuals, and outlined lyric captions.",
    useCase: "Same-character MV repair",
    accentClass: "showcase-d",
    proof: ["Character-sheet continuity", "Repaired scene geometry", "Outlined lyric captions"]
  },
  {
    slug: "walk-away-in-red",
    artist: "VÉLA",
    title: "Walk Away In Red",
    duration: "2:11",
    style: "Trend-led cinematic dance-pop",
    theme: "Red-jacket city escape, loyal crew, rooftop release energy",
    tagline: "A same-character story MV built from trend research, character-sheet continuity, and a full cinematic release pipeline.",
    useCase: "Trend-to-release MV",
    accentClass: "showcase-a",
    proof: ["Trend brief", "Character-sheet continuity", "Cinematic MV"]
  },
  {
    slug: "i-got-my-glow-back",
    artist: "VÉLA",
    title: "I Got My Glow Back",
    duration: "2:11",
    style: "Neon comeback pop",
    theme: "Rainy city confidence, clean hero styling, late-night glow-up",
    tagline: "A confident VÉLA release showing how a single visual identity can carry a chorus hook across a full MV package.",
    useCase: "Comeback single MV",
    accentClass: "showcase-b",
    proof: ["Hook-first song", "Consistent hero look", "Release-ready captions"]
  },
  {
    slug: "city-says-yes",
    artist: "VÉLA",
    title: "City Says Yes",
    duration: "2:11",
    style: "Urban optimism pop",
    theme: "Night city lights, motion, celebration, and cinematic street energy",
    tagline: "A city-scale pop MV built to show upbeat campaign storytelling, public-space visuals, and branded release polish.",
    useCase: "Urban campaign MV",
    accentClass: "showcase-c",
    proof: ["City story arc", "Lyric captions", "Branded ending sheet"]
  },
  {
    slug: "lights-on-me",
    artist: "VÉLA",
    title: "Lights On Me",
    duration: "2:11",
    style: "Cinematic resilience pop",
    theme: "Rain-lit city, stage glow, emotional comeback energy",
    tagline: "A full song-to-MV release proving Zolo can move from prompt to finished cinematic music video.",
    useCase: "Artist launch MV",
    accentClass: "showcase-d",
    proof: ["Music + lyrics", "Cinematic MV", "Release-ready credits"]
  },
  {
    slug: "stay-a-little",
    artist: "VÉLA",
    title: "Stay A Little",
    duration: "2:11",
    style: "Romantic soul-pop",
    theme: "Warm urban evening, rain reflections, boutique cafe intimacy",
    tagline: "A softer emotional release showing Zolo's ability to package mood, story, lyrics, and visuals together.",
    useCase: "Premium ballad MV",
    accentClass: "showcase-a",
    proof: ["Moodboarded scenes", "Lyric timing", "Branded ending sheet"]
  },
  {
    slug: "too-good-at-being-okay",
    artist: "VÉLA",
    title: "Too Good At Being Okay",
    duration: "2:11",
    style: "Modern jazz-pop",
    theme: "Late-night jazz club, blue smoke, amber lights, elegant heartbreak",
    tagline: "A polished genre shift that makes the platform credible for indie artists and high-end brand stories.",
    useCase: "Genre campaign MV",
    accentClass: "showcase-b",
    proof: ["Genre-specific visuals", "Performance edit", "Commercial pack"]
  },
  {
    slug: "tap-tap",
    artist: "VÉLA",
    title: "Tap Tap",
    duration: "2:11",
    style: "Digital commerce pop",
    theme: "Shopping energy, mobile rhythm, clean hook, upbeat girl-group launch",
    tagline: "A commercial-pop showcase for brands that need original jingles, short-form hooks, and vertical MV assets.",
    useCase: "Brand jingle + MV",
    accentClass: "showcase-c",
    proof: ["Brand hook", "MV title cards", "Social-ready edit"]
  }
] as const satisfies readonly ShowcaseRelease[];

export type ShowcaseSlug = (typeof showcaseReleases)[number]["slug"];

export function getShowcaseRelease(slug: string) {
  return showcaseReleases.find((release) => release.slug === slug);
}
